import { useState, useRef, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import styled from "styled-components";
import { breakpoint, theme } from "@utilities";
import { InputLabel } from "@mui/material";
import { StyledFormControl } from "@components";
import { CalendarMonth } from "@mui/icons-material";

interface SeparateDatePickerProps {
    value: Date | null | undefined;
    onChange: (date: Date | null) => void;
    label?: string;
    name?: string;
    handleBlur?: any;
    maxDate?: Date;
    minDate?: Date;
    labelBold?: boolean;
}

export const SeparateDatePicker = ({
    value,
    onChange,
    label = "Date of birth",
    maxDate = new Date(),
    minDate = new Date("1/1/1900"),
    handleBlur,
}: SeparateDatePickerProps) => {
    const [showCalendar, setShowCalendar] = useState(false);
    const defaultYear = maxDate ? maxDate.getFullYear() : new Date().getFullYear();
    const defaultMonth = maxDate ? maxDate.getMonth() : new Date().getMonth();
    const [currentMonth, setCurrentMonth] = useState(
        value ? value.getMonth() : defaultMonth,
    );
    const [currentYear, setCurrentYear] = useState(
        value ? value.getFullYear() : defaultYear,
    );
    const [selectedDate, setSelectedDate] = useState<Date | null>(value || null);

    const [dayVal, setDayVal] = useState(value ? String(value.getDate()).padStart(2, "0") : "");
    const [monthVal, setMonthVal] = useState(value ? String(value.getMonth() + 1).padStart(2, "0") : "");
    const [yearVal, setYearVal] = useState(value ? String(value.getFullYear()) : "");

    const dayRef = useRef<HTMLInputElement>(null);
    const monthRef = useRef<HTMLInputElement>(null);
    const yearRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (value) {
            setSelectedDate(value);
            setDayVal(String(value.getDate()).padStart(2, "0"));
            setMonthVal(String(value.getMonth() + 1).padStart(2, "0"));
            setYearVal(String(value.getFullYear()));
        } else {
            setSelectedDate(null);
            setDayVal("");
            setMonthVal("");
            setYearVal("");
        }
    }, [value]);

    const [popupPosition, setPopupPosition] = useState({ top: 0, left: 0, width: 0 });
    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (showCalendar) {
            document.body.style.overflow = "hidden";
        } else {
            document.body.style.overflow = "";
        }
        return () => {
            document.body.style.overflow = "";
        };
    }, [showCalendar]);

    useEffect(() => {
        if (showCalendar && containerRef.current) {
            const rect = containerRef.current.getBoundingClientRect();
            const isMobile = window.innerWidth <= 768;
            const calendarHeight = 400;
            const calendarWidth = isMobile ? 280 : 360;
            const spaceBelow = window.innerHeight - rect.bottom;

            const openBelow = spaceBelow >= calendarHeight;

            let top: number;
            if (openBelow) {
                top = rect.bottom + 4;
            } else {
                top = Math.max(8, rect.top - calendarHeight - 4);
            }

            let left: number;
            if (isMobile) {
                left = (window.innerWidth - calendarWidth) / 2;
            } else {
                left = Math.min(
                    rect.right - calendarWidth,
                    window.innerWidth - calendarWidth - 8,
                );
                left = Math.max(8, left);
            }

            setPopupPosition({ top, left, width: rect.width });
        }
    }, [showCalendar]);

    const months = [
        "Jan", "Feb", "Mar", "Apr", "May", "Jun",
        "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
    ];

    const daysOfWeek = ["S", "M", "T", "W", "T", "F", "S"];

    const getDaysInMonth = (month: number, year: number) => {
        return new Date(year, month + 1, 0).getDate();
    };

    const getFirstDayOfMonth = (month: number, year: number) => {
        return new Date(year, month, 1).getDay();
    };

    const isDateInRange = useCallback((date: Date) => {
        const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
        const min = new Date(minDate.getFullYear(), minDate.getMonth(), minDate.getDate());
        const max = new Date(maxDate.getFullYear(), maxDate.getMonth(), maxDate.getDate());
        return d >= min && d <= max;
    }, [minDate, maxDate]);

    const tryCommitTypedDate = useCallback((d: string, m: string, y: string) => {
        const day = parseInt(d, 10);
        const month = parseInt(m, 10);
        const year = parseInt(y, 10);

        if (!d || !m || !y || y.length < 4) return;
        if (isNaN(day) || isNaN(month) || isNaN(year)) return;
        if (month < 1 || month > 12) return;
        if (day < 1 || day > getDaysInMonth(month - 1, year)) return;

        const date = new Date(year, month - 1, day);
        if (isDateInRange(date)) {
            setSelectedDate(date);
            setCurrentMonth(date.getMonth());
            setCurrentYear(date.getFullYear());
            onChange(date);
        }
    }, [onChange, isDateInRange]);

    const handleDayChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const v = e.target.value.replace(/\D/g, "").slice(0, 2);
        setDayVal(v);
        if (v.length === 2) {
            monthRef.current?.focus();
            monthRef.current?.select();
        }
        tryCommitTypedDate(v, monthVal, yearVal);
    };

    const handleMonthChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const v = e.target.value.replace(/\D/g, "").slice(0, 2);
        setMonthVal(v);
        if (v.length === 2) {
            yearRef.current?.focus();
            yearRef.current?.select();
        }
        tryCommitTypedDate(dayVal, v, yearVal);
    };

    const handleYearChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const v = e.target.value.replace(/\D/g, "").slice(0, 4);
        setYearVal(v);
        tryCommitTypedDate(dayVal, monthVal, v);
    };

    const handleFieldBlur = () => {
        if (dayVal && dayVal.length === 1) setDayVal(dayVal.padStart(2, "0"));
        if (monthVal && monthVal.length === 1) setMonthVal(monthVal.padStart(2, "0"));
        tryCommitTypedDate(
            dayVal.length === 1 ? dayVal.padStart(2, "0") : dayVal,
            monthVal.length === 1 ? monthVal.padStart(2, "0") : monthVal,
            yearVal,
        );
        if (handleBlur && typeof handleBlur === "function") {
            try { handleBlur(); } catch { }
        }
    };

    const handleFieldKeyDown = (
        e: React.KeyboardEvent<HTMLInputElement>,
        field: "day" | "month" | "year",
    ) => {
        if (e.key === "Backspace") {
            const target = e.target as HTMLInputElement;
            if (target.value === "") {
                if (field === "month") { dayRef.current?.focus(); dayRef.current?.select(); }
                if (field === "year") { monthRef.current?.focus(); monthRef.current?.select(); }
            }
        }
        if (e.key === "/" || e.key === "-" || e.key === ".") {
            e.preventDefault();
            if (field === "day") { monthRef.current?.focus(); monthRef.current?.select(); }
            if (field === "month") { yearRef.current?.focus(); yearRef.current?.select(); }
        }
    };

    const handleDateClick = (day: number) => {
        const newDate = new Date(currentYear, currentMonth, day);
        if (!isDateInRange(newDate)) return;
        setSelectedDate(newDate);
        setDayVal(String(day).padStart(2, "0"));
        setMonthVal(String(currentMonth + 1).padStart(2, "0"));
        setYearVal(String(currentYear));
        onChange(newDate);
        setShowCalendar(false);
        if (handleBlur && typeof handleBlur === "function") {
            try { handleBlur(); } catch { }
        }
    };

    const handlePrevMonth = (e: React.MouseEvent) => {
        e.preventDefault();
        if (currentMonth === 0) {
            setCurrentMonth(11);
            setCurrentYear(currentYear - 1);
        } else {
            setCurrentMonth(currentMonth - 1);
        }
    };

    const handleNextMonth = (e: React.MouseEvent) => {
        e.preventDefault();
        if (currentMonth === 11) {
            setCurrentMonth(0);
            setCurrentYear(currentYear + 1);
        } else {
            setCurrentMonth(currentMonth + 1);
        }
    };

    const renderCalendar = () => {
        const daysInMonth = getDaysInMonth(currentMonth, currentYear);
        const firstDay = getFirstDayOfMonth(currentMonth, currentYear);
        const prevMonthDays = getDaysInMonth(currentMonth - 1, currentYear);
        const days = [];

        for (let i = firstDay - 1; i >= 0; i--) {
            days.push(
                <Day key={`prev-${i}`} $isOtherMonth>
                    {prevMonthDays - i}
                </Day>,
            );
        }

        for (let day = 1; day <= daysInMonth; day++) {
            const isSelected =
                selectedDate?.getDate() === day &&
                selectedDate?.getMonth() === currentMonth &&
                selectedDate?.getFullYear() === currentYear;
            const dateForDay = new Date(currentYear, currentMonth, day);
            const inRange = isDateInRange(dateForDay);
            days.push(
                <Day
                    key={day}
                    onClick={() => inRange && handleDateClick(day)}
                    $isSelected={isSelected}
                    $isDisabled={!inRange}
                >
                    {day}
                </Day>,
            );
        }

        const remainingDays = 42 - days.length;
        for (let day = 1; day <= remainingDays; day++) {
            days.push(
                <Day key={`next-${day}`} $isOtherMonth>
                    {day}
                </Day>,
            );
        }

        return days;
    };

    const minYear = minDate.getFullYear();
    const maxYear = maxDate.getFullYear();
    const years = Array.from({ length: maxYear - minYear + 1 }, (_, i) => maxYear - i);

    return (
        <Container ref={containerRef}>
            <StyledFormControl variant="standard" sx={{ marginTop: "0 !important" }}>
                <InputLabel shrink>{label}</InputLabel>
                <DateInputRow>
                    <DateSegment
                        ref={dayRef}
                        value={dayVal}
                        onChange={handleDayChange}
                        onBlur={handleFieldBlur}
                        onKeyDown={(e) => handleFieldKeyDown(e, "day")}
                        placeholder="DD"
                        inputMode="numeric"
                        maxLength={2}
                    />
                    <Separator>/</Separator>
                    <DateSegment
                        ref={monthRef}
                        value={monthVal}
                        onChange={handleMonthChange}
                        onBlur={handleFieldBlur}
                        onKeyDown={(e) => handleFieldKeyDown(e, "month")}
                        placeholder="MM"
                        inputMode="numeric"
                        maxLength={2}
                    />
                    <Separator>/</Separator>
                    <DateSegmentYear
                        ref={yearRef}
                        value={yearVal}
                        onChange={handleYearChange}
                        onBlur={handleFieldBlur}
                        onKeyDown={(e) => handleFieldKeyDown(e, "year")}
                        placeholder="YYYY"
                        inputMode="numeric"
                        maxLength={4}
                    />
                    <CalendarButton
                        type="button"
                        onClick={() => setShowCalendar(!showCalendar)}
                        title="Open calendar"
                    >
                        <CalendarMonth sx={{ fontSize: 20 }} />
                    </CalendarButton>
                </DateInputRow>
            </StyledFormControl>
            {showCalendar &&
                createPortal(
                    <>
                        <Overlay
                            onClick={() => {
                                setShowCalendar(false);
                                if (handleBlur && typeof handleBlur === "function") {
                                    try { handleBlur(); } catch { }
                                }
                            }}
                        />
                        <CalendarPopup
                            onClick={(e) => e.stopPropagation()}
                            style={{
                                top: `${popupPosition.top}px`,
                                left: `${popupPosition.left}px`,
                            }}
                        >
                            <CalendarHeader>
                                <NavButton onClick={handlePrevMonth} type="button">
                                    <svg viewBox="0 0 24 24">
                                        <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z" />
                                    </svg>
                                </NavButton>
                                <DropdownWrapper>
                                    <DropdownSelect
                                        value={currentMonth}
                                        onChange={(e) => setCurrentMonth(Number(e.target.value))}
                                    >
                                        {months.map((month, idx) => (
                                            <option key={idx} value={idx}>
                                                {month}
                                            </option>
                                        ))}
                                    </DropdownSelect>
                                    <DropdownSelect
                                        value={currentYear}
                                        onChange={(e) => setCurrentYear(Number(e.target.value))}
                                    >
                                        {years.map((year) => (
                                            <option key={year} value={year}>
                                                {year}
                                            </option>
                                        ))}
                                    </DropdownSelect>
                                </DropdownWrapper>
                                <NavButton onClick={handleNextMonth} type="button">
                                    <svg viewBox="0 0 24 24">
                                        <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z" />
                                    </svg>
                                </NavButton>
                            </CalendarHeader>
                            <DaysHeader>
                                {daysOfWeek.map((day, idx) => (
                                    <DayLabel key={idx}>{day}</DayLabel>
                                ))}
                            </DaysHeader>
                            <DaysGrid>{renderCalendar()}</DaysGrid>
                        </CalendarPopup>
                    </>,
                    document.body,
                )}
        </Container>
    );
};

const Container = styled.div`
    position: relative;
`;

const DateInputRow = styled.div`
    display: flex;
    align-items: center;
    margin-top: 16px;
    border-bottom: 1px solid ${theme.colors.sub_2};
    padding-bottom: 6px;
    gap: 2px;

    &:focus-within {
        border-bottom: 1px solid ${theme.colors.main};
    }
`;

const DateSegment = styled.input`
    border: none;
    outline: none;
    font-size: 16px;
    font-family: inherit;
    width: 28px;
    text-align: center;
    padding: 0;
    background: transparent;
    color: #090A18;

    &::placeholder {
        color: ${theme.colors.gray_1};
    }
`;

const DateSegmentYear = styled(DateSegment)`
    width: 44px;
`;

const Separator = styled.span`
    color: ${theme.colors.gray_1};
    font-size: 16px;
    user-select: none;
    padding: 0 1px;
`;

const CalendarButton = styled.button`
    background: none;
    border: none;
    cursor: pointer;
    color: ${theme.colors.gray_1};
    padding: 2px;
    margin-left: auto;
    display: flex;
    align-items: center;

    &:hover {
        color: ${theme.colors.main};
    }
`;

const Overlay = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.3);
    z-index: 9999;
    overflow: hidden;
`;

const CalendarPopup = styled.div`
    position: fixed;
    background: white;
    border-radius: 12px;
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
    padding: 20px;
    z-index: 10000;
    width: 360px;

    ${breakpoint.breakOnlyMobile`
        width: 320px;
    `}
`;

const CalendarHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 16px;
    gap: 12px;
`;

const NavButton = styled.button`
    background: none;
    border: none;
    cursor: pointer;
    color: #6b7280;
    padding: 4px;
    display: flex;
    align-items: center;
    justify-content: center;

    svg {
        width: 24px;
        height: 24px;
        fill: currentColor;
    }

    &:hover {
        color: #1f2937;
    }

    &:disabled {
        opacity: 0.3;
        cursor: not-allowed;
    }
`;

const DropdownWrapper = styled.div`
    display: flex;
    gap: 8px;
    flex: 1;
`;

const DropdownSelect = styled.select`
    padding: 6px 12px;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    font-size: 14px;
    cursor: pointer;
    background: white;
    flex: 1;

    &:focus {
        outline: none;
        border-color: #2563eb;
    }
`;

const DaysHeader = styled.div`
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    gap: 4px;
    margin-bottom: 8px;
`;

const DayLabel = styled.div`
    text-align: center;
    font-size: 12px;
    color: #9ca3af;
    font-weight: 500;
    padding: 8px 0;
`;

const DaysGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    gap: 4px;
`;

const Day = styled.div<{ $isSelected?: boolean; $isOtherMonth?: boolean; $isDisabled?: boolean }>`
    text-align: center;
    padding: 10px;
    font-size: 14px;
    cursor: ${(props) => (props.$isOtherMonth || props.$isDisabled ? "default" : "pointer")};
    border-radius: 6px;
    color: ${(props) =>
        props.$isOtherMonth ? "#d1d5db" :
        props.$isDisabled ? "#d1d5db" :
        "#1f2937"};
    background: ${(props) => (props.$isSelected ? theme.colors.main : "transparent")};
    font-weight: ${(props) => (props.$isSelected ? "600" : "400")};

    &:hover {
        background: ${(props) =>
            props.$isOtherMonth || props.$isDisabled
                ? "transparent"
                : props.$isSelected
                  ? theme.colors.main
                  : "#f3f4f6"};
    }
`;
